// Provisions (design doc Section 11, LOCKED placeholder rate; consequence
// resolved by 16.62). Consumed passively by crew over real-clock time,
// against the PAUSABLE game clock (effectiveNow) -- unlike Fuel/node-regen,
// Section 11 is explicit that Provisions burn "only while the clock is
// actually running," so docked/logged-out time burns nothing.
import { effectiveNow, type ClockState } from "./gameClock";
import { devTuning } from "./devTuning";
import type { AppState } from "./ui/state";

const MS_PER_HOUR = 60 * 60 * 1000;

// Section 11's locked placeholder: "1 Provision supports 10 crew for 1
// hour of clock time" = crew/10 per hour, expressed via the dev-tunable
// provisionsPerCrewPerHour (default 0.1) so 16.65's slider can adjust it.
export function provisionsPerHour(crew: number): number {
  return crew * devTuning.provisionsPerCrewPerHour;
}

// Starting capacity -- Section 11 calls the whole rate "deliberately
// arbitrary... not a derived number," so this is sized the same
// provisional way: a full day (24h) of consumption at the current crew
// count and rate.
export function startingProvisionsCapacity(crew: number): number {
  return provisionsPerHour(crew) * 24;
}

// Applies elapsed-time consumption since `lastUpdatedAt` (an effectiveNow
// epoch ms). Never goes negative. Pure -- callers write the result back.
export function consumeProvisions(
  provisionsOnBoard: number,
  crew: number,
  lastUpdatedAt: number,
  clock: ClockState,
  nowMs: number
): { provisionsOnBoard: number; lastUpdatedAt: number } {
  const now = effectiveNow(clock, nowMs);
  const elapsedHours = Math.max(0, (now - lastUpdatedAt) / MS_PER_HOUR);
  const consumed = provisionsPerHour(crew) * elapsedHours;
  return { provisionsOnBoard: Math.max(0, provisionsOnBoard - consumed), lastUpdatedAt: now };
}

// Stateful wrapper, same "recomputed fresh, return whether it changed"
// convention as shipUpkeep.ts's runHiredShipUpkeep -- called from
// app.ts's periodic checks. A Loaner Ship carries no Provisions pool of
// its own (16.63), so this is a no-op while flying one.
export function runProvisionsTick(state: AppState, nowMs: number): boolean {
  if (!state.currentShip || state.isLoanerShip) {
    return false;
  }
  const before = state.provisionsOnBoard;
  const result = consumeProvisions(
    state.provisionsOnBoard,
    state.currentShip.crew,
    state.provisionsLastUpdatedAt,
    state.clock,
    nowMs
  );
  state.provisionsOnBoard = result.provisionsOnBoard;
  state.provisionsLastUpdatedAt = result.lastUpdatedAt;
  return Math.abs(before - state.provisionsOnBoard) >= 0.01;
}
